export type ImportField =
  | "ignore"
  | "firstName"
  | "lastName"
  | "name"
  | "position"
  | "jerseyNumber"
  | "email"
  | "role"

export type ImportRole = "admin" | "coach" | "player" | "parent"

export type TeamMemberImportRowInput = {
  sourceIndex: number
  firstName?: string
  lastName?: string
  name?: string
  email?: string
  role?: ImportRole
  jerseyNumber?: string
  position?: string
}

export type ColumnMapping = Record<number, ImportField>

const textDecoder = new TextDecoder()

const compact = (value: unknown): string => String(value ?? "").trim()

export function parseDelimitedRows(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ""
  let inQuotes = false

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i]
    const next = text[i + 1]

    if (char === "\"") {
      if (inQuotes && next === "\"") {
        cell += "\""
        i += 1
      } else {
        inQuotes = !inQuotes
      }
      continue
    }

    if (!inQuotes && (char === "," || char === "\t")) {
      row.push(cell.trim())
      cell = ""
      continue
    }

    if (!inQuotes && (char === "\n" || char === "\r")) {
      if (char === "\r" && next === "\n") {
        i += 1
      }
      row.push(cell.trim())
      if (row.some((value) => value !== "")) {
        rows.push(row)
      }
      row = []
      cell = ""
      continue
    }

    cell += char
  }

  row.push(cell.trim())
  if (row.some((value) => value !== "")) {
    rows.push(row)
  }
  return normalizeRowWidth(rows)
}

function normalizeRowWidth(rows: string[][]): string[][] {
  const width = Math.max(0, ...rows.map((row) => row.length))
  return rows.map((row) => Array.from({ length: width }, (_, index) => compact(row[index])))
}

function columnNameToIndex(name: string): number {
  let index = 0
  for (const char of name.toUpperCase()) {
    index = index * 26 + (char.charCodeAt(0) - 64)
  }
  return index - 1
}

function elements(parent: Document | Element, localName: string): Element[] {
  return Array.from(parent.getElementsByTagNameNS("*", localName))
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const Decompression = (
    globalThis as unknown as {
      DecompressionStream?: new (format: string) => DecompressionStream
    }
  ).DecompressionStream

  if (!Decompression) {
    throw new Error("This browser cannot read compressed XLSX files.")
  }

  const blobPart = data.buffer.slice(
    data.byteOffset,
    data.byteOffset + data.byteLength
  ) as ArrayBuffer
  const stream = new Blob([blobPart]).stream().pipeThrough(new Decompression("deflate-raw"))
  const buffer = await new Response(stream).arrayBuffer()
  return new Uint8Array(buffer)
}

async function readZipEntries(buffer: ArrayBuffer): Promise<Map<string, Uint8Array>> {
  const view = new DataView(buffer)
  let eocdOffset = -1
  for (
    let offset = view.byteLength - 22;
    offset >= Math.max(0, view.byteLength - 66000);
    offset -= 1
  ) {
    if (view.getUint32(offset, true) === 0x06054b50) {
      eocdOffset = offset
      break
    }
  }

  if (eocdOffset === -1) {
    throw new Error("Could not read XLSX zip directory.")
  }

  const entryCount = view.getUint16(eocdOffset + 10, true)
  let centralOffset = view.getUint32(eocdOffset + 16, true)
  const entries = new Map<string, Uint8Array>()

  for (let i = 0; i < entryCount; i += 1) {
    if (view.getUint32(centralOffset, true) !== 0x02014b50) {
      throw new Error("Invalid XLSX central directory.")
    }

    const compression = view.getUint16(centralOffset + 10, true)
    const compressedSize = view.getUint32(centralOffset + 20, true)
    const nameLength = view.getUint16(centralOffset + 28, true)
    const extraLength = view.getUint16(centralOffset + 30, true)
    const commentLength = view.getUint16(centralOffset + 32, true)
    const localHeaderOffset = view.getUint32(centralOffset + 42, true)
    const nameBytes = new Uint8Array(buffer, centralOffset + 46, nameLength)
    const name = textDecoder.decode(nameBytes)

    if (view.getUint32(localHeaderOffset, true) !== 0x04034b50) {
      throw new Error("Invalid XLSX local file header.")
    }

    const localNameLength = view.getUint16(localHeaderOffset + 26, true)
    const localExtraLength = view.getUint16(localHeaderOffset + 28, true)
    const dataOffset = localHeaderOffset + 30 + localNameLength + localExtraLength
    const compressed = new Uint8Array(buffer, dataOffset, compressedSize)

    if (compression === 0) {
      entries.set(name, compressed)
    } else if (compression === 8) {
      entries.set(name, await inflateRaw(compressed))
    }

    centralOffset += 46 + nameLength + extraLength + commentLength
  }

  return entries
}

function parseSharedStrings(xml: string | undefined): string[] {
  if (!xml) return []
  const document = new DOMParser().parseFromString(xml, "application/xml")
  return elements(document, "si").map((item) =>
    elements(item, "t")
      .map((text) => text.textContent ?? "")
      .join("")
  )
}

function parseWorksheetRows(xml: string, sharedStrings: string[]): string[][] {
  const document = new DOMParser().parseFromString(xml, "application/xml")
  const rows: string[][] = []

  for (const rowElement of elements(document, "row")) {
    const row: string[] = []
    let fallbackColumn = 0
    for (const cellElement of elements(rowElement, "c")) {
      const ref = cellElement.getAttribute("r")
      const column = ref ? columnNameToIndex(ref.replace(/\d+/g, "")) : fallbackColumn
      fallbackColumn = column + 1
      const type = cellElement.getAttribute("t")
      const valueElement = elements(cellElement, "v")[0]
      let value = valueElement?.textContent ?? ""

      if (type === "s") {
        value = sharedStrings[Number(value)] ?? ""
      } else if (type === "inlineStr") {
        value = elements(cellElement, "t")
          .map((text) => text.textContent ?? "")
          .join("")
      }

      row[column] = compact(value)
    }

    if (row.some((value) => compact(value) !== "")) {
      rows.push(row)
    }
  }

  return normalizeRowWidth(rows)
}

async function parseXlsxRows(file: File): Promise<string[][]> {
  const entries = await readZipEntries(await file.arrayBuffer())
  const sharedStrings = parseSharedStrings(
    entries.has("xl/sharedStrings.xml")
      ? textDecoder.decode(entries.get("xl/sharedStrings.xml"))
      : undefined
  )
  const worksheetEntry =
    entries.get("xl/worksheets/sheet1.xml") ??
    [...entries.entries()].find(([name]) => name.startsWith("xl/worksheets/sheet"))?.[1]

  if (!worksheetEntry) {
    throw new Error("No worksheet found in XLSX file.")
  }

  return parseWorksheetRows(textDecoder.decode(worksheetEntry), sharedStrings)
}

export async function parseRosterFile(file: File): Promise<string[][]> {
  const lowerName = file.name.toLowerCase()
  if (lowerName.endsWith(".xlsx")) {
    return parseXlsxRows(file)
  }
  return parseDelimitedRows(await file.text())
}

export function inferColumnMapping(rows: string[][]): {
  mapping: ColumnMapping
  hasHeader: boolean
} {
  const firstRow = rows[0] ?? []
  const normalizedHeaders = firstRow.map((value) => compact(value).toLowerCase())
  const hasHeader = normalizedHeaders.some((value) =>
    [
      "email",
      "e-mail",
      "first",
      "first name",
      "last",
      "last name",
      "jersey",
      "#",
      "position",
      "pos",
    ].includes(value)
  )

  const mapping: ColumnMapping = {}
  const source = hasHeader ? normalizedHeaders : []

  if (hasHeader) {
    source.forEach((header, index) => {
      if (["first", "first name", "firstname"].includes(header)) mapping[index] = "firstName"
      else if (["last", "last name", "lastname"].includes(header)) mapping[index] = "lastName"
      else if (["name", "player", "player name"].includes(header)) mapping[index] = "name"
      else if (["email", "e-mail", "email address"].includes(header)) mapping[index] = "email"
      else if (["jersey", "jersey #", "#", "number", "no"].includes(header))
        mapping[index] = "jerseyNumber"
      else if (["position", "pos"].includes(header)) mapping[index] = "position"
      else if (["role"].includes(header)) mapping[index] = "role"
      else mapping[index] = "ignore"
    })
  } else {
    mapping[0] = "firstName"
    mapping[1] = "lastName"
    mapping[2] = "position"
    mapping[3] = "jerseyNumber"
    mapping[4] = "email"
    for (let index = 5; index < firstRow.length; index += 1) {
      mapping[index] = "ignore"
    }
  }

  for (let index = 0; index < firstRow.length; index += 1) {
    mapping[index] ??= "ignore"
  }

  return { mapping, hasHeader }
}

export function buildImportRows(
  rows: string[][],
  mapping: ColumnMapping,
  hasHeader: boolean,
  defaultRole: ImportRole
): TeamMemberImportRowInput[] {
  const dataRows = hasHeader ? rows.slice(1) : rows
  return dataRows
    .map((row, index) => {
      const input: TeamMemberImportRowInput = {
        sourceIndex: hasHeader ? index + 2 : index + 1,
        role: defaultRole,
      }

      row.forEach((value, columnIndex) => {
        const field = mapping[columnIndex] ?? "ignore"
        const text = compact(value)
        if (!text || field === "ignore") return
        if (field === "role") {
          const role = text.toLowerCase()
          if (role === "admin" || role === "coach" || role === "player" || role === "parent") {
            input.role = role
          }
          return
        }
        input[field] = text
      })

      return input
    })
    .filter((row) =>
      [row.firstName, row.lastName, row.name, row.email, row.jerseyNumber, row.position].some(
        (value) => compact(value) !== ""
      )
    )
}
